import React, { useState, useEffect } from 'react';
import { showError, showSuccess, showDeleteConfirm } from '../../utils/sweetAlert';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { API_ENDPOINTS } from '../../config/api';
import EnhancedMediaLibrary from './EnhancedMediaLibrary';
import Loader from '../shared/Loader';

const PRODUCT_TYPES = [
  { value: 'cuadro', label: 'Cuadro', icon: 'fa-image' },
  { value: 'remera', label: 'Remera', icon: 'fa-shirt' },
  { value: 'almohadon', label: 'Almohadón', icon: 'fa-couch' },
  { value: 'otro', label: 'Otro', icon: 'fa-box' },
];

const CustomizableProductManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [sizeInput, setSizeInput] = useState('');
  const [tagInput, setTagInput] = useState('');

  const [customFieldInput, setCustomFieldInput] = useState({ label: '', placeholder: '', required: false, fieldType: 'text', options: '' });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    basePrice: '',
    productType: 'cuadro',
    images: [],
    sizes: [],
    tags: [],
    customFields: [],
    isActive: true,
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  // ========== PRODUCTOS ==========

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.customizableProducts.getAll(true));
      const data = await res.json();
      setProducts(data);
    } catch (error) {
      showError('Error al cargar productos personalizables');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', description: '', basePrice: '', productType: 'cuadro',
      images: [], sizes: [], tags: [], customFields: [], isActive: true,
    });
    setCustomFieldInput({ label: '', placeholder: '', required: false, fieldType: 'text', options: '' });
    setEditingId(null);
    setShowForm(false);
    setSizeInput('');
    setTagInput('');
  };

  const handleDuplicate = (product) => {
    setFormData({
      name: product.name + ' (copia)',
      description: product.description || '',
      basePrice: product.basePrice,
      productType: product.productType,
      images: product.images || [],
      sizes: product.sizes || [],
      tags: product.tags || [],
      customFields: product.customFields || [],
      isActive: true,
    });
    setEditingId(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEdit = (product) => {
    setFormData({
      name: product.name,
      description: product.description || '',
      basePrice: product.basePrice,
      productType: product.productType,
      images: product.images || [],
      sizes: product.sizes || [],
      tags: product.tags || [],
      customFields: product.customFields || [],
      isActive: product.isActive,
    });
    setEditingId(product._id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.basePrice || !formData.description) {
      return showError('Completá nombre, precio y descripción');
    }

    try {
      const url = editingId
        ? API_ENDPOINTS.customizableProducts.update(editingId)
        : API_ENDPOINTS.customizableProducts.create();
      const method = editingId ? 'PUT' : 'POST';

      const payload = { ...formData, basePrice: Number(formData.basePrice) };
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Error al guardar');
      showSuccess(editingId ? 'Producto actualizado' : 'Producto creado');
      resetForm();
      fetchProducts();
    } catch (error) {
      showError(error.message);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await showDeleteConfirm('¿Eliminar este producto personalizable?');
    if (!confirmed) return;
    try {
      const res = await fetch(API_ENDPOINTS.customizableProducts.delete(id), { method: 'DELETE' });
      if (!res.ok) throw new Error();
      showSuccess('Producto eliminado');
      fetchProducts();
    } catch {
      showError('Error al eliminar');
    }
  };

  const addSize = () => {
    const trimmed = sizeInput.trim();
    if (trimmed && !formData.sizes.includes(trimmed)) {
      setFormData(prev => ({ ...prev, sizes: [...prev.sizes, trimmed] }));
    }
    setSizeInput('');
  };

  const removeSize = (s) => {
    setFormData(prev => ({ ...prev, sizes: prev.sizes.filter(x => x !== s) }));
  };

  const addTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !formData.tags.includes(trimmed)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, trimmed] }));
    }
    setTagInput('');
  };

  const removeTag = (t) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(x => x !== t) }));
  };

  const handleMediaSelect = (image) => {
    const url = image.url || image;
    if (!formData.images.includes(url)) {
      setFormData(prev => ({ ...prev, images: [...prev.images, url] }));
    }
    setShowMediaLibrary(false);
  };

  const removeImage = (url) => {
    setFormData(prev => ({ ...prev, images: prev.images.filter(i => i !== url) }));
  };

  const typeIcon = (type) => PRODUCT_TYPES.find(t => t.value === type)?.icon || 'fa-box';

  // ========== RENDER ==========

  if (loading) return <Loader />;

  return (
    <div>
      {/* Header */}
      <div className="dashboard-header">
        <h1>
          <i className="fas fa-wand-magic-sparkles" style={{ marginRight: '10px' }}></i>
          Personalizado IA
        </h1>
        <p style={{ color: '#666', margin: '5px 0 15px' }}>
          Gestión de cuadros, remeras y almohadones personalizables. Los pedidos se ven en la sección Órdenes.
        </p>
      </div>

      {/* ====== PRODUCTOS ====== */}
      <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <span style={{ color: '#666' }}>{products.length} producto(s)</span>
            <button className="btn btn-success" onClick={() => { resetForm(); setShowForm(true); }}>
              <i className="fas fa-plus" style={{ marginRight: '6px' }}></i> Nuevo Producto
            </button>
          </div>

          {/* Formulario */}
          {showForm && (
            <div style={{
              background: '#fff', border: '1px solid #dee2e6', borderRadius: '12px',
              padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}>
              <h4 style={{ marginBottom: '20px' }}>
                <i className={`fas ${editingId ? 'fa-edit' : 'fa-plus-circle'}`} style={{ marginRight: '8px' }}></i>
                {editingId ? 'Editar Producto' : 'Nuevo Producto Personalizable'}
              </h4>
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Nombre */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Nombre *</label>
                    <input
                      type="text" className="form-control"
                      value={formData.name}
                      onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                      placeholder="Ej: Cuadro Personalizado 30x40"
                    />
                  </div>
                  {/* Tipo */}
                  <div className="col-md-3 mb-3">
                    <label className="form-label fw-bold">Tipo *</label>
                    <select
                      className="form-select"
                      value={formData.productType}
                      onChange={e => setFormData(p => ({ ...p, productType: e.target.value }))}
                    >
                      {PRODUCT_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  {/* Precio */}
                  <div className="col-md-3 mb-3">
                    <label className="form-label fw-bold">Precio Base *</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input
                        type="number" className="form-control"
                        value={formData.basePrice}
                        onChange={e => setFormData(p => ({ ...p, basePrice: e.target.value }))}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Descripción */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Descripción *</label>
                  <textarea
                    className="form-control" rows="3"
                    value={formData.description}
                    onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                    placeholder="Descripción del producto personalizable..."
                  />
                </div>

                {/* Tamaños */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Tamaños disponibles</label>
                  <div className="input-group mb-2">
                    <input
                      type="text" className="form-control"
                      value={sizeInput}
                      onChange={e => setSizeInput(e.target.value)}
                      placeholder="Ej: S, M, L, XL o 30x40, 50x70..."
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSize(); } }}
                    />
                    <button type="button" className="btn btn-outline-secondary" onClick={addSize}>
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {formData.sizes.map((s, i) => (
                      <span key={i} className="badge bg-secondary" style={{ fontSize: '0.85rem', padding: '6px 10px', cursor: 'pointer' }}
                        onClick={() => removeSize(s)}>
                        {s} <i className="fas fa-times" style={{ marginLeft: '4px' }}></i>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Tags */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Tags</label>
                  <div className="input-group mb-2">
                    <input
                      type="text" className="form-control"
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      placeholder="Agregar tag..."
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    />
                    <button type="button" className="btn btn-outline-secondary" onClick={addTag}>
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {formData.tags.map((t, i) => (
                      <span key={i} className="badge bg-info text-dark" style={{ fontSize: '0.85rem', padding: '6px 10px', cursor: 'pointer' }}
                        onClick={() => removeTag(t)}>
                        {t} <i className="fas fa-times" style={{ marginLeft: '4px' }}></i>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Imágenes */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Imágenes de ejemplo / Mockups</label>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {formData.images.map((url, i) => (
                      <div key={i} style={{ position: 'relative', width: '100px', height: '100px' }}>
                        <img
                          src={getDisplayImageUrl(url)} alt={`img-${i}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', border: '1px solid #dee2e6' }}
                        />
                        <button type="button" onClick={() => removeImage(url)}
                          style={{
                            position: 'absolute', top: '-6px', right: '-6px',
                            background: '#dc3545', color: '#fff', border: 'none',
                            borderRadius: '50%', width: '22px', height: '22px',
                            fontSize: '11px', cursor: 'pointer', display: 'flex',
                            alignItems: 'center', justifyContent: 'center'
                          }}>
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={() => setShowMediaLibrary(true)}
                      style={{
                        width: '100px', height: '100px', border: '2px dashed #adb5bd',
                        borderRadius: '8px', background: 'transparent', cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', color: '#6c757d', fontSize: '0.8rem'
                      }}>
                      <i className="fas fa-plus" style={{ fontSize: '1.2rem', marginBottom: '4px' }}></i>
                      Agregar
                    </button>
                  </div>
                </div>

                {/* Campos personalizados */}
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    <i className="fas fa-list-check" style={{ marginRight: '6px', color: '#6f42c1' }}></i>
                    Campos del producto (datos que completa el cliente)
                  </label>
                  <p style={{ fontSize: '0.82rem', color: '#888', margin: '-4px 0 10px' }}>
                    Ej: "Tu nombre aquí", "Fecha de nacimiento", "Club", etc.
                  </p>
                  <div className="row g-2 mb-2">
                    <div className="col-md-3">
                      <input type="text" className="form-control form-control-sm"
                        placeholder="Nombre del campo *"
                        value={customFieldInput.label}
                        onChange={e => setCustomFieldInput(p => ({ ...p, label: e.target.value }))} />
                    </div>
                    <div className="col-md-3">
                      <input type="text" className="form-control form-control-sm"
                        placeholder="Placeholder (opcional)"
                        value={customFieldInput.placeholder}
                        onChange={e => setCustomFieldInput(p => ({ ...p, placeholder: e.target.value }))} />
                    </div>
                    <div className="col-md-2">
                      <select className="form-select form-select-sm"
                        value={customFieldInput.fieldType}
                        onChange={e => setCustomFieldInput(p => ({ ...p, fieldType: e.target.value }))}>
                        <option value="text">Texto</option>
                        <option value="number">Número</option>
                        <option value="date">Fecha</option>
                        <option value="select">Selección</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <div className="form-check" style={{ paddingTop: '6px' }}>
                        <input type="checkbox" className="form-check-input" id="cfRequired"
                          checked={customFieldInput.required}
                          onChange={e => setCustomFieldInput(p => ({ ...p, required: e.target.checked }))} />
                        <label className="form-check-label" htmlFor="cfRequired" style={{ fontSize: '0.85rem' }}>Requerido</label>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <button type="button" className="btn btn-sm btn-outline-secondary w-100"
                        onClick={() => {
                          if (!customFieldInput.label.trim()) return;
                          const newField = {
                            label: customFieldInput.label.trim(),
                            placeholder: customFieldInput.placeholder.trim(),
                            required: customFieldInput.required,
                            fieldType: customFieldInput.fieldType,
                            options: customFieldInput.fieldType === 'select' ? customFieldInput.options.split(',').map(o => o.trim()).filter(Boolean) : [],
                          };
                          setFormData(p => ({ ...p, customFields: [...p.customFields, newField] }));
                          setCustomFieldInput({ label: '', placeholder: '', required: false, fieldType: 'text', options: '' });
                        }}>
                        <i className="fas fa-plus"></i> Agregar
                      </button>
                    </div>
                  </div>
                  {customFieldInput.fieldType === 'select' && (
                    <div className="mb-2">
                      <input type="text" className="form-control form-control-sm"
                        placeholder="Opciones separadas por coma: Opción 1, Opción 2, Opción 3"
                        value={customFieldInput.options}
                        onChange={e => setCustomFieldInput(p => ({ ...p, options: e.target.value }))} />
                    </div>
                  )}
                  {formData.customFields.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {formData.customFields.map((field, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          background: '#f8f9fa', borderRadius: '8px', padding: '8px 12px'
                        }}>
                          <i className="fas fa-grip-vertical" style={{ color: '#ccc' }}></i>
                          <strong style={{ fontSize: '0.9rem' }}>{field.label}</strong>
                          <span className="badge bg-light text-dark" style={{ fontSize: '0.75rem' }}>{field.fieldType}</span>
                          {field.required && <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>Requerido</span>}
                          {field.placeholder && <span style={{ fontSize: '0.8rem', color: '#888' }}>"{field.placeholder}"</span>}
                          {field.options?.length > 0 && <span style={{ fontSize: '0.8rem', color: '#6f42c1' }}>({field.options.join(', ')})</span>}
                          <button type="button" style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }}
                            onClick={() => setFormData(p => ({ ...p, customFields: p.customFields.filter((_, idx) => idx !== i) }))}>
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Activo */}
                <div className="form-check mb-3">
                  <input
                    type="checkbox" className="form-check-input" id="isActiveCheck"
                    checked={formData.isActive}
                    onChange={e => setFormData(p => ({ ...p, isActive: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="isActiveCheck">Producto activo</label>
                </div>

                {/* Botones */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="submit" className="btn btn-success">
                    <i className={`fas ${editingId ? 'fa-save' : 'fa-plus'}`} style={{ marginRight: '6px' }}></i>
                    {editingId ? 'Guardar Cambios' : 'Crear Producto'}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Listado de productos */}
          {products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
              <i className="fas fa-wand-magic-sparkles" style={{ fontSize: '3rem', marginBottom: '15px', display: 'block' }}></i>
              <h5>No hay productos personalizables</h5>
              <p>Creá tu primer producto para que los clientes puedan personalizar con su foto.</p>
            </div>
          ) : (
            <div className="row">
              {products.map(product => (
                <div key={product._id} className="col-md-4 col-lg-3 mb-4">
                  <div style={{
                    background: '#fff', borderRadius: '12px', overflow: 'hidden',
                    border: '1px solid #dee2e6', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    opacity: product.isActive ? 1 : 0.6, transition: 'all 0.3s'
                  }}>
                    {/* Imagen */}
                    <div style={{ position: 'relative', paddingTop: '75%', background: '#f8f9fa' }}>
                      {product.images && product.images[0] ? (
                        <img
                          src={getDisplayImageUrl(product.images[0])}
                          alt={product.name}
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <div style={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#adb5bd'
                        }}>
                          <i className={`fas ${typeIcon(product.productType)}`} style={{ fontSize: '2.5rem' }}></i>
                        </div>
                      )}
                      {/* Badge tipo */}
                      <span style={{
                        position: 'absolute', top: '8px', left: '8px',
                        background: '#1f8e38', color: '#fff', padding: '3px 10px',
                        borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600'
                      }}>
                        <i className={`fas ${typeIcon(product.productType)}`} style={{ marginRight: '4px' }}></i>
                        {PRODUCT_TYPES.find(t => t.value === product.productType)?.label || product.productType}
                      </span>
                      {!product.isActive && (
                        <span style={{
                          position: 'absolute', top: '8px', right: '8px',
                          background: '#dc3545', color: '#fff', padding: '3px 10px',
                          borderRadius: '20px', fontSize: '0.75rem'
                        }}>Inactivo</span>
                      )}
                    </div>
                    {/* Info */}
                    <div style={{ padding: '14px' }}>
                      <h6 style={{ margin: '0 0 6px', fontWeight: '600' }}>{product.name}</h6>
                      <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 8px',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {product.description}
                      </p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1f8e38' }}>
                          ${product.basePrice?.toLocaleString()}
                        </span>
                        {product.sizes && product.sizes.length > 0 && (
                          <span style={{ fontSize: '0.75rem', color: '#888' }}>
                            {product.sizes.length} tamaño(s)
                          </span>
                        )}
                      </div>
                      {/* Acciones */}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <button className="btn btn-sm btn-outline-primary" style={{ flex: 1 }}
                          onClick={() => handleEdit(product)}>
                          <i className="fas fa-edit"></i> Editar
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" title="Duplicar"
                          onClick={() => handleDuplicate(product)}>
                          <i className="fas fa-copy"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(product._id)}>
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* ====== MODAL MEDIA LIBRARY ====== */}
      {showMediaLibrary && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 9999, padding: '20px'
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px', maxWidth: '900px', width: '100%',
            maxHeight: '85vh', overflowY: 'auto', padding: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h5 style={{ margin: 0 }}>Seleccionar Imagen</h5>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowMediaLibrary(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <EnhancedMediaLibrary
              onSelect={handleMediaSelect}
              mode="selector"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomizableProductManager;
